import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-step-guide-type-2';

/**
 * Step-by-step instructional guide for a SignNow workflow.
 * Full-width heading + subtitle header on top, then a 2-column numbered grid of steps below.
 * Optimizes for many short steps via a wide grid, unlike type-1 which uses a side intro and single-column list.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for the step-by-step guide to {keyword}. 6-10 words.
 * @slot subtitle - {content} <me-text variant="large"> - Introductory paragraph for the grid guide. You MUST write 15-30 words.
 * @example
 *   <sn-step-guide-type-2
 *     items='[{"title":"Identification of Parties","text":"Clearly state the names."},{"title":"Scope of Rights","text":"Specify the rights being granted."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Step-by-Step Guide</me-heading>
 *     <me-text slot="subtitle" variant="large">Understanding the key elements.</me-text>
 *   </sn-step-guide-type-2>
 */
@Component({
  tag: 'sn-step-guide-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcStepByStepGuide {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Numbered step items displayed in a 2-column grid.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Step action label. 1-5 words.
   * @item.property {string} text - Brief actionable instruction for this step. 5-15 words.
   * @item.required title
   * @item.content title text
   * @minItems 6
   * @maxItems 6
   */
  @Prop({ mutable: true }) items?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} items - Fallback step items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        items?: { title?: string; text?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="column" yGapLg="none">
          <me-column spanLg="12">
            <div class={`${COMPONENT_NAME}__header`}>
              <slot name="heading"></slot>
              <slot name="subtitle"></slot>
            </div>
          </me-column>
          {items.length > 0 && (
            <me-column spanLg="12">
              <div class={`${COMPONENT_NAME}__grid`}>
                {items.map((item, index) => (
                  <div class={`${COMPONENT_NAME}__step`}>
                    <span class={`${COMPONENT_NAME}__number`}>{String(index + 1).padStart(2, '0')}</span>
                    <div class={`${COMPONENT_NAME}__step-content`}>
                      {item.title && <me-heading tag="h4" class={`${COMPONENT_NAME}__step-title`}>{item.title}:</me-heading>}
                      {item.text && <me-text tag="span"> {item.text}</me-text>}
                    </div>
                  </div>
                ))}
              </div>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
